import {
  UserPen,
  ChevronDown,
  Calendar,
  CreditCard,
  Edit,
  Bell,
  Download,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import contactsApi from "../api/contacts";
import axiosInstance from "../api/axiosInstance";
import { fetchUsageSafe } from "../api/usage";
import { useSubmissionLock } from "../hooks/useSubmissionLock";

function Contact() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  // tenancy + activity
  const [tenancy, setTenancy] = useState(null);
  const [activity, setActivity] = useState([]);
  const [onboarding, setOnboarding] = useState({
    tenantPortalActive: false,
    invitePending: false,
    latestInvite: null,
  });

  // ✅ HMO rooms state
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState(null);

  // tenancy form state
  const [tenancyForm, setTenancyForm] = useState({
    propertyId: "",
    roomId: "", // ✅ NEW
    rentAmountGBP: "",
    startDate: "",
  });
  const [tenancyStatus, setTenancyStatus] = useState(null);

  const [usage, setUsage] = useState(null);
  const { run: runSubmission, isPending } = useSubmissionLock();

  useEffect(() => {
    let alive = true;

    fetchUsageSafe().then((data) => {
      if (alive) setUsage(data);
    });

    return () => {
      alive = false;
    };
  }, []);

  // ✅ Query params for room-first flow (Property -> Contact)
  const queryParams = useMemo(() => {
    try {
      return new URLSearchParams(location.search);
    } catch {
      return new URLSearchParams();
    }
  }, [location.search]);

  const preselectPropertyId = queryParams.get("propertyId") || "";
  const preselectRoomId = queryParams.get("roomId") || "";

  useEffect(() => {
    async function fetchContact() {
      try {
        setLoading(true);

        const response = await contactsApi.getContactById(id);

        const data =
          response?.data?.contact ??
          response?.data?.data ??
          response?.data ??
          null;

        setContact(data);
      } catch (error) {
        console.error("Failed to fetch contact:", error);
        setContact(null);
      } finally {
        setLoading(false);
      }
    }

    fetchContact();
  }, [id]);

  useEffect(() => {
    if (!contact?.id) return;

    async function fetchTenancyData() {
      try {
        const res = await axiosInstance.get(
          `/api/tenancies/by-contact/${contact.id}`,
          { withCredentials: true }
        );

        const data = res?.data ?? null;
        setTenancy(data?.tenancy || null);
        setActivity(data?.activity || []);
        setOnboarding({
          tenantPortalActive: Boolean(data?.onboarding?.tenantPortalActive),
          invitePending: Boolean(data?.onboarding?.invitePending),
          latestInvite: data?.onboarding?.latestInvite || null,
        });
      } catch (err) {
        setTenancy(null);
        setActivity([]);
        setOnboarding({
          tenantPortalActive: false,
          invitePending: false,
          latestInvite: null,
        });
      }
    }

    fetchTenancyData();
  }, [contact?.id]);

  // ✅ Autofill Tenant Onboarding form when tenancy exists
  useEffect(() => {
    if (!tenancy) return;

    setTenancyForm((prev) => ({
      ...prev,
      propertyId: tenancy.propertyId || "",
      roomId: tenancy.roomId || "",
      rentAmountGBP: tenancy.rentAmountGBP || "",
      startDate: tenancy.startDate ? tenancy.startDate.slice(0, 10) : "",
    }));
  }, [tenancy]);

  // ✅ Merge both direct Property[] and join table, then DEDUPE by property.id
  const mergedProperties = useMemo(() => {
    const raw = [
      ...(contact?.Property || []),
      ...((contact?.properties?.map((p) => p.property).filter(Boolean)) || []),
    ];
    return Array.from(new Map(raw.map((p) => [p.id, p])).values());
  }, [contact]);

  // ✅ Find selected property details (for HMO detection)
  const selectedProperty = useMemo(() => {
    return mergedProperties.find((p) => p.id === tenancyForm.propertyId) || null;
  }, [mergedProperties, tenancyForm.propertyId]);

  const isHmo = (p) => ((p?.type ?? "").toString().toLowerCase() === "hmo");

  // ✅ Apply preselect params ONLY if no tenancy exists (don’t override existing tenancy)
  useEffect(() => {
    if (!preselectPropertyId) return;
    if (tenancy) return;

    setTenancyForm((prev) => ({
      ...prev,
      propertyId: prev.propertyId || preselectPropertyId,
      roomId: prev.roomId || preselectRoomId,
    }));
  }, [preselectPropertyId, preselectRoomId, tenancy]);

  // ✅ Load rooms when selected property is HMO
  useEffect(() => {
    async function loadRooms() {
      setRooms([]);
      setRoomsError(null);

      if (!tenancyForm.propertyId) return;
      if (!isHmo(selectedProperty)) return;

      try {
        setRoomsLoading(true);
        const res = await axiosInstance.get(
          `/api/properties/${tenancyForm.propertyId}/rooms`,
          { withCredentials: true }
        );

        const list = Array.isArray(res?.data) ? res.data : [];
        setRooms(list);

        // If roomId already set (via query param), keep it.
        // If not set and only 1 room exists, auto-select it.
        setTenancyForm((prev) => {
          if (prev.roomId) return prev;
          if (list.length === 1 && list[0]?.id) return { ...prev, roomId: list[0].id };
          return prev;
        });
      } catch (err) {
        console.error("Failed to load rooms:", err);
        setRoomsError(err?.response?.data?.error || err.message);
        setRooms([]);
      } finally {
        setRoomsLoading(false);
      }
    }

    loadRooms();
  }, [tenancyForm.propertyId, selectedProperty]);

  const handleDelete = async () => {
    await runSubmission(`contact:${id}:delete`, async () => {
      if (!window.confirm("Are you sure you want to delete this contact?")) return;

      try {
        await contactsApi.deleteContact(id);
        alert("Contact deleted successfully.");
        navigate("/contacts");
      } catch (error) {
        console.error("Failed to delete contact:", error);
        alert("Failed to delete contact. Please try again.");
      }
    });
  };

  if (loading) return <p className="text-white">Loading contact details...</p>;
  if (!contact) return <p className="text-white">Contact not found.</p>;

  const isTenant =
    contact?.relationship?.toLowerCase() === "tenant";

  const handleCreateTenancy = async (e) => {
    e.preventDefault();
    if (isPending(`contact:${id}:tenancy`)) return;

    await runSubmission(`contact:${id}:tenancy`, async () => {
    setTenancyStatus(null);

    if (tenancy) {
      if (onboarding.tenantPortalActive) {
        setTenancyStatus({
          type: "success",
          message: "Tenant portal is already active for this tenant.",
        });
        return;
      }

      try {
        const res = await axiosInstance.post(
          `/api/tenancies/${tenancy.id}/send-invite`,
          {},
          { withCredentials: true }
        );

        const body = res?.data ?? {};
        setOnboarding({
          tenantPortalActive: Boolean(body?.onboarding?.tenantPortalActive),
          invitePending: Boolean(body?.onboarding?.invitePending ?? body?.inviteSent),
          latestInvite: body?.onboarding?.latestInvite || null,
        });
        setTenancyStatus({
          type: "success",
          message: "Onboarding email sent.",
        });
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message;
        console.error(err);
        setTenancyStatus({
          type: "error",
          message: msg || "Failed to send onboarding email.",
        });
      }

      return;
    }

    if (!tenancyForm.propertyId || !tenancyForm.rentAmountGBP || !tenancyForm.startDate) {
      setTenancyStatus({
        type: "error",
        message: "Please fill in all fields.",
      });
      return;
    }

    // ✅ HMO requires a room selection
    if (isHmo(selectedProperty) && !tenancyForm.roomId) {
      setTenancyStatus({
        type: "error",
        message: "Please select a room for this HMO property.",
      });
      return;
    }

    try {
      const payload = {
        contactId: contact.id,
        propertyId: tenancyForm.propertyId,
        rentAmountGBP: Number(tenancyForm.rentAmountGBP),
        startDate: tenancyForm.startDate,
        sendInvite: true,
      };

      // ✅ Include roomId only for HMO properties
      if (isHmo(selectedProperty)) {
        payload.roomId = tenancyForm.roomId;
      }

      const res = await axiosInstance.post("/api/tenancies", payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      const body = res?.data ?? {};

      const createdTenancy = body?.tenancy || body;
      setTenancy(createdTenancy);

      // ✅ Refresh tenancy + activity from canonical endpoint
      try {
        const tRes = await axiosInstance.get(
          `/api/tenancies/by-contact/${contact.id}`,
          { withCredentials: true }
        );
        const tData = tRes?.data ?? null;
        setTenancy(tData?.tenancy || createdTenancy);
        setActivity(tData?.activity || []);
        setOnboarding({
          tenantPortalActive: Boolean(tData?.onboarding?.tenantPortalActive),
          invitePending: Boolean(tData?.onboarding?.invitePending),
          latestInvite: tData?.onboarding?.latestInvite || null,
        });
      } catch {
        // ignore
      }

      if (body?.onboarding) {
        setOnboarding({
          tenantPortalActive: Boolean(body.onboarding.tenantPortalActive),
          invitePending: Boolean(body.onboarding.invitePending ?? body.onboarding.inviteSent),
          latestInvite: body.onboarding.latestInvite || null,
        });
      }

      setTenancyStatus({
        type: body?.inviteEmailError ? "error" : "success",
        message: body?.inviteEmailError
          ? `Tenancy created, but the onboarding email failed to send: ${body.inviteEmailError}`
          : body?.inviteSent
          ? "Tenancy created and onboarding email sent."
          : "Tenancy created successfully.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message;
      console.error(err);
      setTenancyStatus({
        type: "error",
        message: msg || "Something went wrong while creating tenancy.",
      });
    }
    });
  };

  // ACTIONS
  const handleAdjustRent = async () => {
    if (!tenancy) return;
    if (isPending(`tenancy:${tenancy.id}:adjust-rent`)) return;

    const newRent = prompt("Enter new monthly rent (£)");
    if (!newRent) return;

    await runSubmission(`tenancy:${tenancy.id}:adjust-rent`, async () => {
      try {
        await axiosInstance.post(
          `/api/tenancies/${tenancy.id}/adjust-rent`,
          { rentAmountGBP: Number(newRent) },
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          }
        );

        // ✅ Refresh tenancy + activity
        try {
          const tRes = await axiosInstance.get(
            `/api/tenancies/by-contact/${contact.id}`,
            { withCredentials: true }
          );
          const tData = tRes?.data ?? null;
          setTenancy(tData?.tenancy || null);
          setActivity(tData?.activity || []);
          return;
        } catch {
          // ignore
        }

        // fallback: update locally
        setTenancy((prev) => (prev ? { ...prev, rentAmountGBP: Number(newRent) } : prev));
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message;
        console.error("Adjust rent failed:", err);
        alert(msg || "Failed to adjust rent.");
      }
    });
  };

  const handleSendReminder = async () => {
    if (!tenancy) return;
    if (isPending(`tenancy:${tenancy.id}:send-reminder`)) return;

    await runSubmission(`tenancy:${tenancy.id}:send-reminder`, async () => {
      try {
        const res = await axiosInstance.post(
          `/api/tenancies/${tenancy.id}/send-reminder`,
          {},
          { withCredentials: true }
        );

        const body = res?.data ?? {};
        const createdActivity =
          body?.activity ||
          body?.notification ||
          body?.reminder ||
          null;

        if (createdActivity?.id) {
          setActivity((prev) => [createdActivity, ...prev]);
        } else {
          setActivity((prev) => [
            {
              id: `local-reminder-${Date.now()}`,
              createdAt: new Date().toISOString(),
              description: "Payment reminder sent.",
            },
            ...prev,
          ]);
        }

        alert("Payment reminder sent");
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message;
        console.error("Send reminder failed:", err);
        alert(msg || "Failed to send reminder.");
      }
    });
  };

  const handleDownloadReceipt = () => {
    if (!tenancy) return;
    window.open(`/api/tenancies/${tenancy.id}/latest-receipt`, "_blank");
  };

  const onboardingSubmitDisabled =
    !usage?.isEnhanced ||
    mergedProperties.length === 0 ||
    Boolean(tenancy && onboarding.tenantPortalActive) ||
    isPending(`contact:${id}:tenancy`);

  const onboardingButtonLabel = !tenancy
    ? "Send onboarding email / create tenancy"
    : onboarding.tenantPortalActive
    ? "Tenant portal active"
    : isPending(`contact:${id}:tenancy`)
    ? tenancy
      ? "Sending..."
      : "Creating..."
    : onboarding.invitePending
    ? "Resend onboarding email"
    : "Send onboarding email";

  return (
    <div className="pt-10 md:pt-0">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl text-white">Contact Information</h1>
        <button
          onClick={() => navigate(`/edit-contact/${id}`)}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white border border-white/10 rounded-xl hover:bg-[#AF9B8A]/20 transition backdrop-blur-md"
        >
          <UserPen className="w-5 h-5" />
          <span>Edit Contact</span>
        </button>
      </div>

      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6" />

      {/* Contact Info */}
      <section className="glass p-6 rounded-xl mb-6 text-white">
        <h2 className="text-2xl font-bold">
          {contact.name ||
            `${contact.firstName || ""} ${contact.lastName || ""}`.trim()}
        </h2>
        <p>
          <strong>Email:</strong>{" "}
          <a
            href={`mailto:${contact.email}`}
            className="hover:text-[#AF9B8A] transition"
          >
            {contact.email || "N/A"}
          </a>
        </p>
        <p>
          <strong>Phone:</strong> {contact.phone || "N/A"}
        </p>
        <p>
          <strong>Role:</strong>{" "}
          {contact.relationship
            ? contact.relationship[0].toUpperCase() +
              contact.relationship.slice(1)
            : "N/A"}
        </p>
        <p>
          <strong>Property:</strong>{" "}
          {mergedProperties.length > 0
            ? mergedProperties.map((property) => (
                <span key={property.id}>
                  {property.title} — {property.address1}, {property.town}
                  <br />
                </span>
              ))
            : "N/A"}
        </p>
        <p>
          <strong>Move-in Date:</strong> {contact.moveInDate || "N/A"}
        </p>
      </section>

        {/* Tenant Onboarding */}
        {isTenant && (
        <div className="glass p-6 rounded-xl lg:col-span-2 gap-6 mb-6">
        <h2 className="text-2xl font-bold mb-4">Tenant Onboarding</h2>

        {!usage?.isEnhanced && (
          <div className="mb-4 p-4 rounded-lg bg-[#AF9B8A]/20 border border-[#AF9B8A]/40 text-sm">
            Tenant Portal is a <strong>Beem Enhanced</strong> feature.
            <br />
            Upgrade to onboard tenants and give them portal access.
          </div>
        )}

        {tenancy && onboarding.tenantPortalActive && (
          <div className="mb-4 p-4 rounded-lg bg-white/5 border border-white/10 text-sm">
            <strong>Tenant portal active.</strong> You can still adjust rent
            or send reminders below.
          </div>
        )}

        {tenancy && !onboarding.tenantPortalActive && (
          <div className="mb-4 p-4 rounded-lg bg-white/5 border border-white/10 text-sm">
            <strong>Tenancy created.</strong> Tenant portal setup is not complete yet.
            Send the onboarding email below.
          </div>
        )}

        <form
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          onSubmit={handleCreateTenancy}
        >
          <div>
            <label className="block text-sm font-semibold text-white/80 uppercase mb-3">
              Select property
            </label>
            <div className="relative">
              <select
                className="appearance-none w-full p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                value={tenancyForm.propertyId}
                onChange={(e) =>
                  setTenancyForm((f) => ({
                    ...f,
                    propertyId: e.target.value,
                    roomId: "", // ✅ reset when switching property
                  }))
                }
              >
                <option value="" disabled>
                  Choose property
                </option>
                {mergedProperties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.title} — {property.address1}, {property.town}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-white/70" />
            </div>
          </div>

          {/* ✅ HMO rooms dropdown */}
          {isHmo(selectedProperty) && (
            <div>
              <label className="block text-sm font-semibold text-white/80 uppercase mb-3">
                Select room
              </label>

              {roomsError && (
                <p className="text-red-400 text-sm mb-2">{roomsError}</p>
              )}

              <div className="relative">
                <select
                  className="appearance-none w-full p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  value={tenancyForm.roomId}
                  onChange={(e) =>
                    setTenancyForm((f) => ({
                      ...f,
                      roomId: e.target.value,
                    }))
                  }
                  disabled={roomsLoading}
                >
                  <option value="" disabled>
                    {roomsLoading ? "Loading rooms..." : "Choose room"}
                  </option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-white/70" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-white/80 uppercase mb-3">
              Rent amount (per month, GBP)
            </label>
            <input
              type="number"
              className="appearance-none w-full p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
              value={tenancyForm.rentAmountGBP}
              onChange={(e) =>
                setTenancyForm((f) => ({
                  ...f,
                  rentAmountGBP: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white/80 uppercase mb-3">
              Tenancy start date
            </label>
            <input
              type="date"
              className="appearance-none w-full p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
              value={tenancyForm.startDate}
              onChange={(e) =>
                setTenancyForm((f) => ({
                  ...f,
                  startDate: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white/80 uppercase mb-3">
              Notes to the tenant
            </label>
            <textarea
              className="appearance-none w-full p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
              rows={3}
            />
          </div>

          <div className="md:col-span-2">
            <button
              type="submit"
              className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition backdrop-blur-md ${
                onboardingSubmitDisabled
                  ? "bg-white/5 text-white/40 border-white/5 cursor-not-allowed"
                  : "bg-white/10 text-white border-white/10 hover:bg-[#AF9B8A]/20"
              }`}
              disabled={onboardingSubmitDisabled}
            >
              {onboardingButtonLabel}
            </button>

            {tenancyStatus && (
              <p
                className={`text-sm mt-2 ${
                  tenancyStatus.type === "success"
                    ? "text-green-400"
                    : "text-red-400"
                }`}
              >
                {tenancyStatus.message}
              </p>
            )}
          </div>
        </form>
      </div>
        )}

      {/* Tenant Overview */}
      {isTenant && (
      <div className="glass p-6 rounded-xl lg:col-span-2 gap-6 mb-6">

        {tenancy ? (
          <>
            <p className="block text-sm font-semibold text-white/80 uppercase mb-3">
              Moved in on:{" "}
              {tenancy.startDate
                ? new Date(tenancy.startDate).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : "—"}
            </p>

            <p className="text-lg font-semibold mb-2">
              £{Number(tenancy.rentAmountGBP || 0).toLocaleString()}
            </p>

            <div className="flex items-center text-gray-300 text-sm mb-1">
              <Calendar className="w-4 h-4 mr-2" />
              Rent due monthly
            </div>

            {tenancy.paymentMethod && (
              <div className="flex items-center text-gray-300 text-sm mb-4">
                <CreditCard className="w-4 h-4 mr-2" />
                {tenancy.paymentMethod}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleAdjustRent}
                disabled={isPending(`tenancy:${tenancy.id}:adjust-rent`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white/10 border border-white/10 rounded-lg hover:bg-[#AF9B8A]/30 transition disabled:opacity-50"
                aria-busy={isPending(`tenancy:${tenancy.id}:adjust-rent`)}
              >
                <Edit className="w-4 h-4" /> {isPending(`tenancy:${tenancy.id}:adjust-rent`) ? "Updating..." : "Adjust Rent"}
              </button>

              <button
                onClick={handleSendReminder}
                disabled={isPending(`tenancy:${tenancy.id}:send-reminder`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white/10 border border-white/10 rounded-lg hover:bg-[#AF9B8A]/30 transition disabled:opacity-50"
                aria-busy={isPending(`tenancy:${tenancy.id}:send-reminder`)}
              >
                <Bell className="w-4 h-4" /> {isPending(`tenancy:${tenancy.id}:send-reminder`) ? "Sending..." : "Send Reminder"}
              </button>

              <button
                onClick={handleDownloadReceipt}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white/10 border border-white/10 rounded-lg hover:bg-[#AF9B8A]/30 transition"
              >
                <Download className="w-4 h-4" /> Receipt
              </button>
            </div>
          </>
        ) : (
          <p className="text-gray-400">No active tenancy</p>
        )}
      </div>
      )}

      {/* Tenant Activity */}
      {isTenant && (
      <div className="glass p-6 rounded-xl lg:col-span-2">
        <h2 className="text-2xl font-bold mb-4">Tenant Activity</h2>

        {activity.length > 0 ? (
          <ul className="space-y-4">
            {activity.map((item) => (
              <li key={item.id} className="border-b border-white/20 pb-4">
                <p className="font-semibold">
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : ""}
                </p>
                <p className="text-white/80">{item.description}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400">No activity yet</p>
        )}
      </div>
      )}

      {/* Delete Contact */}
      <div className="flex justify-end mt-6">
        <button
          onClick={handleDelete}
          disabled={isPending(`contact:${id}:delete`)}
          className="px-5 py-2 rounded-xl text-white font-semibold transition border border-red-700 bg-rose-900/40 hover:bg-red-700 disabled:opacity-50"
          aria-busy={isPending(`contact:${id}:delete`)}
        >
          {isPending(`contact:${id}:delete`) ? "Deleting..." : "Delete Contact"}
        </button>
      </div>
    </div>
  );
}

export default Contact;
